import React from 'react';
import { Link } from 'react-router-dom';

const Button = ({
    children,
    type = 'button',
    variant = 'primary',
    size = 'md',
    className = '',
    isLoading = false,
    disabled = false,
    href,
    to,
    onClick,
    ...props
}) => {
    // Base classes
    const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

    // Size classes
    const sizeClasses = {
        sm: "px-3 py-1.5 text-xs",
        md: "px-4 py-2 text-sm",
        lg: "px-6 py-3 text-base",
    };

    // Variant classes
    const variantClasses = {
        primary: "bg-gold-gradient text-white border-none hover:shadow-[0_0_20px_rgba(201,162,77,0.4)] hover:opacity-90 focus:ring-gold",
        secondary: "bg-bg-card border border-gold/40 text-gold hover:bg-gold/10 focus:ring-gold",
        success: "bg-success-600 text-white hover:bg-success-700 focus:ring-success-500",
        danger: "bg-danger-600 text-white hover:bg-danger-700 focus:ring-danger-500",
        warning: "bg-warning-500 text-white hover:bg-warning-600 focus:ring-warning-500",
        info: "bg-info-600 text-white hover:bg-info-700 focus:ring-info-500",
        outline: "border border-gold/30 bg-transparent text-text hover:bg-gold/10 hover:border-gold focus:ring-gold",
        ghost: "bg-transparent text-text-muted hover:text-text hover:bg-white/5 focus:ring-gold",
        // Specific lighter variants for secondary actions - kept for backwards compatibility but styled darker
        'primary-light': "bg-primary-900/50 text-gold hover:bg-primary-900 focus:ring-primary-500",
        'success-light': "bg-success-900/50 text-success-400 hover:bg-success-900 focus:ring-success-500",
        'danger-light': "bg-danger-900/50 text-danger-400 hover:bg-danger-900 focus:ring-danger-500",
        'info-light': "bg-info-900/50 text-info-400 hover:bg-info-900 focus:ring-info-500",
        'warning-light': "bg-warning-900/50 text-warning-400 hover:bg-warning-900 focus:ring-warning-500",
    };

    const classes = `
        ${baseClasses}
        ${sizeClasses[size] || sizeClasses.md}
        ${variantClasses[variant] || variantClasses.primary}
        ${className}
    `.trim().replace(/\s+/g, ' ');

    // Loading Spinner
    const Spinner = () => (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
    );

    // Render as react-router Link
    if (to) {
        return (
            <Link to={to} className={classes} {...props}>
                {isLoading && <Spinner />}
                {children}
            </Link>
        );
    }

    // Render as HTML Anchor
    if (href) {
        return (
            <a href={href} className={classes} {...props}>
                {isLoading && <Spinner />}
                {children}
            </a>
        );
    }

    // Render as Button
    return (
        <button
            type={type}
            className={classes}
            disabled={disabled || isLoading}
            onClick={onClick}
            {...props}
        >
            {isLoading && <Spinner />}
            {children}
        </button>
    );
};

export default Button;
